import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SpeedIcon from '@mui/icons-material/Speed';
import { Button, SvgIcon } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip';
import { Box } from '@mui/system';
import { ReactComponent as CleanAirLevelIcon } from 'assets/images/icons/Clean_Air_Level.svg';
import { ReactComponent as Co2Icon } from 'assets/images/icons/CO2.svg';
import { ReactComponent as ComfortLevelIcon } from 'assets/images/icons/Comfort_Level.svg';
import { ReactComponent as NoxIcon } from 'assets/images/icons/Nox.svg';
import { ReactComponent as OccupancyIcon } from 'assets/images/icons/OCCUPANCY.svg';
import { ReactComponent as PM25Icon } from 'assets/images/icons/pm2.5.svg';
import { ReactComponent as PM10Icon } from 'assets/images/icons/pm10.svg';
import { ReactComponent as RHIcon } from 'assets/images/icons/rh.svg';
import { ReactComponent as TempIcon } from 'assets/images/icons/TEMP.svg';
import { ReactComponent as TvocIcon } from 'assets/images/icons/TVOC.svg';
import { ReactComponent as VentilationLevelIcon } from 'assets/images/icons/Ventilation_Level.svg';
import RefreshButton from 'components/RefreshButton';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleGraphs } from 'stores/sensor/sensorStore';
import { brandColors } from 'types/colors';
import { Measurement } from 'types/measurement';

const iconsArray = [
  VentilationLevelIcon,
  CleanAirLevelIcon,
  ComfortLevelIcon,
  Co2Icon,
  RHIcon, // RH -> need fix
  TempIcon,
  PM25Icon,
  PM10Icon,
  TvocIcon,
  OccupancyIcon,
];

const chartFields = [
  Measurement.Co2Ratio,
  Measurement.CAL,
  Measurement.COMFORT_LEVEL,
  // Measurement.Co2,
  Measurement.Co2,
  Measurement.RelativeHumidity,
  Measurement.Temperature,
  // Measurement.PM2,
  Measurement.PM2,
  // Measurement.PM10,
  Measurement.PM10,
  Measurement.TVOC,
  Measurement.occupancy,
];

const chartLabels = {
  [Measurement.Co2Ratio]: 'Ventilation',
  [Measurement.CAL]: 'Clean Air Level',
  [Measurement.COMFORT_LEVEL]: 'Comfort Level',
  [Measurement.Co2]: 'CO2',
  [Measurement.RelativeHumidity]: 'RH',
  [Measurement.Temperature]: 'Temperature',
  [Measurement.PM2]: 'PM 2.5',
  [Measurement.PM10]: 'PM 10',
  [Measurement.TVOC]: 'TVOC',
  [Measurement.occupancy]: 'Occupancy',
  nox: 'NOx',
  motion_counter: 'Motion',
  [Measurement.DpMean]: 'Differential Pressure',
  [Measurement.assigned_hvac_equipment_state]: 'HVAC Power',
  thermal_served: 'Thermal Served',
  air_served: 'Air Served',
  thermal_justified: 'Thermal Justified',
  air_justified: 'Air Justified',
  thermal_requested: 'Thermal Requested',
  air_requested: 'Air Requested',
  thermal_saveable: 'Thermal Saveable',
  air_saveable: 'Air Saveable',
};

// border: '2px solid red',
const GraphsFilterBar = ({
  hasNox = false,
  hasRadar = false,
  hasDpMean = false,
  hideTvoc = false,
  isMobileMode = false,
  isChartSelectionDialog = false,
  showRefresh = true,
  onConfirm,
  chartOptionsOverride,
  twoLineLabels = false,
  selectedGraphs: controlledSelectedGraphs,
  onSelectedGraphsChange,
}) => {
  const iconScale = isMobileMode ? '1.25' : hasNox ? '2.0' : '1.5';
  const labelFontSize = isMobileMode ? '.75rem' : '.9rem';
  const currentUser = useSelector(state => state.sensors.currentUser);
  const isAdminUser = currentUser?.role === 's';

  const chartOptions = useMemo(() => {
    if (chartOptionsOverride) {
      return chartOptionsOverride.filter(option => isAdminUser || !option.chartField.endsWith('_served'));
    }

    const options = chartFields
      .map((chartField, index) => ({
        chartField,
        icon: iconsArray[index],
      }))
      .filter(option => !(hideTvoc && option.chartField === Measurement.TVOC));

    if (hasNox) {
      options.push({ chartField: 'nox', icon: NoxIcon });
    }
    if (hasRadar) {
      options.push({ chartField: 'motion_counter', icon: OccupancyIcon });
    }
    if (hasDpMean) {
      options.push({ chartField: Measurement.DpMean, icon: SpeedIcon });
    }

    options.push(
      { chartField: Measurement.assigned_hvac_equipment_state, icon: PowerSettingsNewIcon },
      { chartField: Measurement.thermal_served, icon: ComfortLevelIcon, color: brandColors.BLACK },
      { chartField: Measurement.thermal_justified, icon: ComfortLevelIcon, color: brandColors.BLUE },
      { chartField: Measurement.thermal_saveable, icon: ComfortLevelIcon, color: brandColors.GREEN },
      { chartField: Measurement.thermal_requested, icon: ComfortLevelIcon, color: brandColors.RED },
      { chartField: Measurement.air_served, icon: VentilationLevelIcon, color: brandColors.BLACK },
      { chartField: Measurement.air_justified, icon: VentilationLevelIcon, color: brandColors.BLUE },
      { chartField: Measurement.air_saveable, icon: VentilationLevelIcon, color: brandColors.GREEN },
      { chartField: Measurement.air_requested, icon: VentilationLevelIcon, color: brandColors.RED },
    );

    return options.filter(option => isAdminUser || !option.chartField.endsWith('_served'));
  }, [chartOptionsOverride, hasNox, hasRadar, hasDpMean, hideTvoc, isAdminUser]);

  const visibleChartFields = useMemo(() => chartOptions.map(option => option.chartField), [chartOptions]);
  const isGridLayout = isMobileMode || isChartSelectionDialog;
  const chartGroups = [
    {
      label: 'Air quality',
      fields: [
        Measurement.Co2Ratio,
        Measurement.CAL,
        Measurement.Co2,
        Measurement.PM2,
        Measurement.PM10,
        Measurement.TVOC,
        'nox',
      ],
    },
    { label: 'Thermal conditions', fields: [Measurement.COMFORT_LEVEL, Measurement.RelativeHumidity, Measurement.Temperature] },
    {
      label: 'Occupancy and HVAC',
      fields: [Measurement.occupancy, 'motion_counter', Measurement.DpMean, Measurement.assigned_hvac_equipment_state],
    },
    {
      label: 'Thermal demand',
      fields: [
        Measurement.thermal_served,
        Measurement.thermal_justified,
        Measurement.thermal_saveable,
        Measurement.thermal_requested,
      ],
    },
    {
      label: 'Air demand',
      fields: [Measurement.air_served, Measurement.air_justified, Measurement.air_saveable, Measurement.air_requested],
    },
  ].map(group => ({ ...group, options: chartOptions.filter(option => group.fields.includes(option.chartField)) }));
  const otherOptions = chartOptions.filter(option => !chartGroups.some(group => group.fields.includes(option.chartField)));
  if (otherOptions.length) {
    chartGroups.push({ label: 'Other', options: otherOptions });
  }

  const selectedGraphsFromStore = useSelector(state => state.sensors.selectedGraphs);
  const selectedGraphs = controlledSelectedGraphs ?? selectedGraphsFromStore;

  const [localSelectedGraphsArray, setLocalSelectedGraphsArray] = useState([...selectedGraphs]);
  const [isRefreshGlowing, setIsRefreshGlowing] = useState(false);
  useEffect(() => {
    setLocalSelectedGraphsArray([...selectedGraphs]);
  }, [selectedGraphs]);
  // const selectedGraphs = useSelector(state => state.sensors.selectedGraphs);
  //   console.log(selectedGraphs);

  const dispatch = useDispatch();

  const handleToggle = graphName => {
    setLocalSelectedGraphsArray(prevSelected =>
      prevSelected.includes(graphName) ? prevSelected.filter(name => name !== graphName) : [...prevSelected, graphName],
    );
    setIsRefreshGlowing(true);
  };

  const handleRefresh = () => {
    setIsRefreshGlowing(false);
    // console.log(localSelectedGraphsArray);
    const visibleSelectedGraphs = localSelectedGraphsArray.filter(graph => visibleChartFields.includes(graph));

    if (visibleSelectedGraphs.length === 0) {
      alert('Please select at least 1 graph');
      return false;
    }
    const selectionChanged =
      visibleSelectedGraphs.length !== selectedGraphs.length ||
      visibleSelectedGraphs.some(graph => !selectedGraphs.includes(graph));
    if (!selectionChanged) {
      return true;
    }
    if (onSelectedGraphsChange) {
      onSelectedGraphsChange(visibleSelectedGraphs);
    } else {
      dispatch(toggleGraphs(visibleSelectedGraphs));
    }
    return true;
  };

  const handleConfirm = () => {
    if (handleRefresh()) {
      onConfirm?.();
    }
  };

  return (
    <FormGroup
      sx={{
        ...(isGridLayout ? { height: 'auto' } : {}),
        width: '100%',
        flexWrap: 'nowrap',
        justifyContent: isMobileMode ? 'flex-start' : undefined,
        boxSizing: 'border-box',
        overflowX: { xs: 'hidden', sm: isGridLayout ? 'visible' : 'auto' },
        overflowY: isGridLayout ? 'visible' : 'hidden',
        px: isGridLayout ? 0 : 1.5,
        pt: isGridLayout ? 0.5 : 1.5,
        pb: isGridLayout ? 0.5 : 1,
        transform: isGridLayout ? 'none' : 'rotateX(180deg)',
        scrollbarWidth: 'thin',
        scrollbarColor: '#3244E2 #E7E9F8',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#E7E9F8',
          borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#3244E2',
          borderRadius: '999px',
          border: '2px solid #E7E9F8',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#2534B8',
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          transform: isGridLayout ? 'none' : 'rotateX(180deg)',
          alignItems: 'flex-start',
          alignContent: isGridLayout ? 'center' : undefined,
          justifyContent: 'center',
          flexWrap: 'nowrap',
          gap: 1,
          minWidth: isGridLayout ? 0 : '100%',
          width: { xs: '100%', sm: isGridLayout ? '100%' : 'max-content' },
          height: 'auto',
          overflow: 'visible',
        }}>
        {showRefresh && (
          <Box
            sx={{
              marginRight: isMobileMode ? '.35rem' : '1rem',
              p: 1.25,
              position: isMobileMode ? 'static' : 'sticky',
              left: 0,
              zIndex: 1,
              // backgroundColor: isMobileMode ? 'transparent' : 'background.paper',
            }}>
            <RefreshButton isGlowing={isRefreshGlowing} onClick={handleRefresh} />
          </Box>
        )}
        {chartGroups
          .filter(group => group.options.length > 0)
          .map(group => (
            <Box
              key={group.label}
              component="section"
              aria-label={group.label}
              sx={{ minWidth: 0, maxWidth: '100%', width: { xs: '100%', sm: isGridLayout ? '100%' : undefined } }}>
              <Box component="h3" sx={{ fontSize: '0.8rem', color: '#626975', fontWeight: 600, m: 0, mb: 0.75 }}>
                {group.label}
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(4, minmax(0, 1fr))',
                    sm: `repeat(${group.options.length}, ${isMobileMode ? '5.5rem' : '7rem'})`,
                  },
                  overflowX: { xs: 'visible', sm: isGridLayout ? 'auto' : 'visible' },
                  gap: { xs: 0.5, sm: 1 },
                }}>
                {group.options.map(({ chartField, icon, color }) => {
                  const chartLabel = chartLabels[chartField];
                  const displayChartLabel = twoLineLabels ? chartLabel.replace(' ', '\n') : chartLabel;
                  const IconComponent = icon;
                  const isSelected = localSelectedGraphsArray.includes(chartField);
                  const option = (
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => handleToggle(chartField)}
                      sx={{
                        width: isMobileMode ? '100%' : { xs: '100%', sm: '7rem' },
                        minWidth: 0,
                        height: { xs: '5rem', sm: isMobileMode ? '5.75rem' : '6.25rem' },
                        m: 0,
                        mr: 0,
                        p: isMobileMode ? 0 : { sm: 0.75 },
                        position: 'relative',
                        border: `2px solid ${isSelected ? brandColors.BLUE : '#E0E0E0'}`,
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: isMobileMode ? 'column' : { sm: 'column' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isMobileMode ? 0.25 : { sm: 0.25 },
                        color,
                        font: 'inherit',
                        touchAction: isMobileMode ? 'manipulation' : undefined,
                        ...(isMobileMode
                          ? {
                              '@media (hover: hover)': {
                                '&:hover': { borderColor: brandColors.BLUE },
                              },
                            }
                          : { '&:hover': { borderColor: brandColors.BLUE } }),
                      }}>
                      {isSelected && (
                        <CheckCircleIcon
                          aria-hidden="true"
                          sx={{ position: 'absolute', top: 4, right: 4, fontSize: '1rem', color: brandColors.BLUE }}
                        />
                      )}
                      <SvgIcon
                        sx={{
                          color: color || '#000',
                          width: { xs: '2.5rem', sm: isMobileMode ? '3.5rem' : '2.5rem' },
                          height: { xs: '2.5rem', sm: isMobileMode ? '3.5rem' : '2.5rem' },
                          // fontSize: isMobileMode ? '2.25rem' : undefined,
                          flexShrink: 0,
                          scale: isMobileMode
                            ? chartField === Measurement.DpMean || chartField === Measurement.assigned_hvac_equipment_state
                              ? 0.8
                              : 1
                            : chartField === Measurement.assigned_hvac_equipment_state
                              ? 1
                              : chartField === Measurement.DpMean
                                ? '1.5'
                                : iconScale,
                        }}>
                        <IconComponent className="HomeIconClass" />
                      </SvgIcon>
                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          fontSize: { xs: '0.7rem', sm: labelFontSize },
                          lineHeight: isMobileMode ? 1.1 : undefined,
                          textAlign: 'center',
                          whiteSpace: isMobileMode || twoLineLabels ? 'pre-line' : { sm: 'normal' },
                          overflowWrap: 'anywhere',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                        }}>
                        {displayChartLabel}
                      </Box>
                    </Box>
                  );

                  return chartField === 'tvoc_avg' || chartField === 'temperature_avg' || chartField === Measurement.DpMean ? (
                    <Tooltip
                      key={chartField}
                      title={
                        chartField === Measurement.DpMean
                          ? 'Each Differential Pressure value is the trimmed mean of readings in its preceding five-minute window.'
                          : `each ${chartLabel} value on the graph is the average value of its last 5 minutes`
                      }>
                      {option}
                    </Tooltip>
                  ) : (
                    <Box key={chartField}>{option}</Box>
                  );
                })}
              </Box>
            </Box>
          ))}
      </Box>
      {onConfirm && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minHeight: '52px',
            flexShrink: 0,
            alignItems: 'center',
            mt: 2,
            position: { xs: 'sticky', sm: 'static' },
            bottom: 0,
            zIndex: 1,
            backgroundColor: '#fff',
          }}>
          <Button variant="contained" size="small" sx={{ minWidth: '7rem' }} onClick={handleConfirm}>
            Confirm
          </Button>
        </Box>
      )}
    </FormGroup>
  );
};

GraphsFilterBar.propTypes = {
  hasNox: PropTypes.bool,
  hasRadar: PropTypes.bool,
  hasDpMean: PropTypes.bool,
  hideTvoc: PropTypes.bool,
  isMobileMode: PropTypes.bool,
  isChartSelectionDialog: PropTypes.bool,
  showRefresh: PropTypes.bool,
  onConfirm: PropTypes.func,
  chartOptionsOverride: PropTypes.arrayOf(
    PropTypes.shape({
      chartField: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      color: PropTypes.string,
    }),
  ),
  twoLineLabels: PropTypes.bool,
  selectedGraphs: PropTypes.arrayOf(PropTypes.string),
  onSelectedGraphsChange: PropTypes.func,
};

export default GraphsFilterBar;
